import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { findNode } from "@/lib/tree/tree";
import { documentStore } from "@/store/store";
import { TreeNode, Tree } from "@/types/tree";
import { FUTURELAYER_HEADER_ELEMENT_TYPE } from "./headers-footers";
import lodash from "lodash";
import { breakdanceUndo } from "@/lib/undo/undo";
import {
  copyColorSchemeClassesToElement
} from "@/lib/singularity/brand/colors/utils";

export const PREFER_OVERLAY_HEADER_CLASS_NAME =
  "futurelayer-prefer-overlay-header";

export const OVERLAY_HEADER_CLASS_NAME = 'futurelayer-header-is-overlay';


export function registerOverlayHeaderActions() {
  const handler = ({ elementId }: { elementId: TreeNode["id"] }) => {
    const headerElement = findHeaderElement();
    if (!headerElement) return;

    if (!isFirstChildInRootAfterHeader(elementId)) return;

    const firstSection = findNode(documentStore.document.tree, elementId);
    if (!firstSection) return;

    breakdanceUndo.ignore(() => {
      if (shouldApplySectionSchemeToHeader(elementId, firstSection)) {
        applySectionSchemeToHeader(firstSection);
        toggleHeaderElementOverlayBehavior(headerElement.id, headerElement, true);
      } else {
        toggleHeaderElementOverlayBehavior(headerElement.id, headerElement, false);
      }
    });
  };

  breakdanceStoreActions.register.onMovedElement(handler);
  breakdanceStoreActions.register.onMountedElement(handler);
}

export function isFirstChildInRootAfterHeader(
  elementId: TreeNode["id"]
): boolean {
  return documentStore.document.tree.root.children[1]?.id === elementId;
}

export function hasFutureLayerPreferOverlayHeaderClass(
  section: TreeNode
): boolean {
  const classes = section.data.properties?.settings?.advanced?.classes;
  if (!Array.isArray(classes)) return false;
  return classes.includes(PREFER_OVERLAY_HEADER_CLASS_NAME);
}

export function findHeaderElement(): TreeNode | undefined {
  if (
    documentStore.document.tree.root.children[0]?.data.type ===
    FUTURELAYER_HEADER_ELEMENT_TYPE
  ) {
    return documentStore.document.tree.root.children[0];
  }
  return undefined;
}

export function setHeaderOverlayBehaviorForPageTree(pageTree: Tree): Tree {
  const headerElement = pageTree.root.children[0];
  if (
    !headerElement ||
    headerElement.data.type !== FUTURELAYER_HEADER_ELEMENT_TYPE
  ) {
    return pageTree;
  }

  const firstSection = pageTree.root.children[1];
  if (!firstSection) {
    return pageTree;
  }

  if (hasFutureLayerPreferOverlayHeaderClass(firstSection)) {
    toggleHeaderElementOverlayBehaviorOnPhantomHeader(headerElement, true);
  }

  return pageTree;
}


export function toggleHeaderElementOverlayBehaviorOnPhantomHeader(
  element: TreeNode,
  enable: boolean
): void {
  const existingClasses = lodash.get(
    element,
    "data.properties.settings.advanced.classes",
    []
  );

  const classesArray = Array.isArray(existingClasses) ? existingClasses : [];
  const filteredClasses = classesArray.filter(cls => cls !== OVERLAY_HEADER_CLASS_NAME);

  const updatedClasses = enable
    ? [...filteredClasses, OVERLAY_HEADER_CLASS_NAME]
    : filteredClasses;

  lodash.set(
    element,
    "data.properties.settings.advanced.classes",
    updatedClasses
  );
}

export function toggleHeaderElementOverlayBehavior(
  elementId: TreeNode["id"],
  element: TreeNode,
  enable: boolean
): void {
  const existingClasses = lodash.get(
    element,
    "data.properties.settings.advanced.classes",
    []
  );

  const classesArray = Array.isArray(existingClasses) ? existingClasses : [];

  const filteredClasses = classesArray.filter(cls => cls !== OVERLAY_HEADER_CLASS_NAME);

  const updatedClasses = enable
    ? [...filteredClasses, OVERLAY_HEADER_CLASS_NAME]
    : filteredClasses;

  documentStore.unthrottledPropertyChanged({
    elementId,
    path: "settings.advanced.classes",
    value: updatedClasses,
    meta: { snapshotLabel: enable ? "Enable header overlay" : "Disable header overlay" }
  });
}

export function applySectionSchemeToHeader(sectionElement: TreeNode): void {
  const headerElement = findHeaderElement();
  if (!headerElement) return;

  copyColorSchemeClassesToElement(sectionElement, headerElement.id, headerElement);
}

export function shouldApplySectionSchemeToHeader(
  sectionElementId: TreeNode["id"],
  sectionElement: TreeNode
): boolean {
  return (
    isFirstChildInRootAfterHeader(sectionElementId) &&
    hasFutureLayerPreferOverlayHeaderClass(sectionElement)
  );
}

/**
 * Check if a header element is currently in overlay mode
 * @param headerElement The header element to check
 * @returns true if the header has the overlay class
 */
export function isHeaderInOverlayMode(headerElement: TreeNode): boolean {
  const classes = headerElement.data.properties?.settings?.advanced?.classes;
  if (!Array.isArray(classes)) return false;
  return classes.includes(OVERLAY_HEADER_CLASS_NAME);
}
